import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { toast } from "sonner";
import { Heart, MessageSquare, Plus, Users, Flame, Clock, Trash2, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const TOPIC_COLORS = {
  General: "#00FFC2", Gaming: "#B980FF", Music: "#FF0055", IRL: "#7CF5FF",
  Creators: "#FFD600", Support: "#00FFC2", Feedback: "#FFD600",
};

export function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function Community() {
  const { user } = useAuth();
  useSeo({
    title: "Community",
    description: "Join the Air Wave community — share streaming tips, ask questions, and connect with fellow creators and viewers.",
  });
  const nav = useNavigate();
  const [posts, setPosts] = useState([]);
  const [topics, setTopics] = useState([]);
  const [topic, setTopic] = useState("All");
  const [sort, setSort] = useState("recent");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", topic: "General" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/community/posts?sort=${sort}&topic=${topic}`)
      .then(({ data }) => { setPosts(data.items); setTopics(data.topics); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, [sort, topic]); // eslint-disable-line

  const submit = async () => {
    if (!form.title.trim() || !form.body.trim()) return;
    setBusy(true);
    try {
      await api.post("/community/posts", form);
      toast.success("Posted to the community 🎉");
      setOpen(false); setForm({ title: "", body: "", topic: "General" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't post"); }
    finally { setBusy(false); }
  };

  const vote = async (p, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { toast("Sign in to react"); return; }
    try {
      const { data } = await api.post(`/community/posts/${p.id}/vote`);
      setPosts((arr) => arr.map((x) => x.id === p.id ? { ...x, votes: data.votes, voted: data.voted } : x));
    } catch (_) {}
  };

  const del = async (p) => {
    try {
      await api.delete(`/community/posts/${p.id}`);
      setPosts((arr) => arr.filter((x) => x.id !== p.id));
      toast("Post deleted");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't delete"); }
  };

  const newPost = () => { if (!user) return nav("/auth"); setOpen(true); };
  const field = "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none transition-colors focus:border-[#00FFC2]";

  return (
    <div className="shell py-8" data-testid="community-page">
      <div className="relative mb-6 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#0C0C14] to-[#101020] p-6">
        <div className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full bg-[#00FFC2]/10 blur-[100px]" />
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Users className="text-[#00FFC2]" size={22} />
              <h1 className="font-display text-2xl font-black tracking-tighter sm:text-3xl">Community</h1>
            </div>
            <p className="mt-1 text-sm text-white/60">Share tips, ask questions, and connect with fellow streamers & viewers.</p>
          </div>
          <Button onClick={newPost} data-testid="community-new-btn"
            className="rounded-full bg-[#00FFC2] font-bold text-black hover:bg-[#00E0A8]">
            <Plus size={16} className="mr-1.5" /> New post
          </Button>
        </div>
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5" data-testid="community-topics">
          {["All", ...topics].map((t) => (
            <button key={t} onClick={() => setTopic(t)} data-testid={`topic-${t}`}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${topic === t ? "bg-[#00FFC2] text-black" : "bg-white/5 text-white/60 hover:bg-white/10"}`}>
              {t}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-1 rounded-full bg-white/5 p-1">
          <button onClick={() => setSort("recent")} data-testid="sort-recent"
            className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${sort === "recent" ? "bg-white/10 text-white" : "text-white/50"}`}>
            <Clock size={13} /> Recent
          </button>
          <button onClick={() => setSort("top")} data-testid="sort-top"
            className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${sort === "top" ? "bg-white/10 text-white" : "text-white/50"}`}>
            <Flame size={13} /> Top
          </button>
        </div>
      </div>

      {loading ? (
        <div className="grid h-40 place-items-center text-white/40">Loading discussions…</div>
      ) : posts.length === 0 ? (
        <div className="glass grid place-items-center rounded-2xl py-16 text-center">
          <Users size={30} className="text-white/20" />
          <p className="mt-3 text-sm text-white/50">No posts yet. Be the first to start a discussion!</p>
          <Button onClick={newPost} className="mt-4 rounded-full bg-[#00FFC2] font-bold text-black">Create the first post</Button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="community-feed">
          {posts.map((p) => {
            const canDelete = user && (user.role === "admin" || p.author_id === user.id);
            const tc = TOPIC_COLORS[p.topic] || "#00FFC2";
            return (
              <Link key={p.id} to={`/community/${p.id}`} data-testid={`post-${p.id}`}
                className="glass card-lift block rounded-2xl p-5 transition-transform">
                <div className="flex items-center gap-2">
                  <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider" style={{ background: `${tc}22`, color: tc }}>{p.topic}</span>
                  <span className="text-xs text-white/40">{p.author_name} · {timeAgo(p.created_at)}</span>
                  {canDelete && (
                    <div className="ml-auto" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button data-testid={`post-menu-${p.id}`} aria-label="Post options"
                            className="grid h-7 w-7 place-items-center rounded-full text-white/40 transition-colors hover:bg-white/10 hover:text-white">
                            <MoreVertical size={15} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem className="text-[#FF0055] focus:text-[#FF0055]" data-testid={`post-delete-${p.id}`}
                            onSelect={() => del(p)}>
                            <Trash2 size={14} className="mr-2" /> Delete post
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </div>
                <h3 className="mt-2 font-display text-lg font-bold leading-tight tracking-tight">{p.title}</h3>
                <p className="mt-1 line-clamp-2 text-sm text-white/60">{p.body}</p>
                <div className="mt-3 flex items-center gap-4">
                  <button onClick={(e) => vote(p, e)} data-testid={`post-vote-${p.id}`}
                    className={`flex items-center gap-1.5 text-sm transition-colors ${p.voted ? "text-[#FF0055]" : "text-white/50 hover:text-white"}`}>
                    <Heart size={15} className={p.voted ? "fill-[#FF0055]" : ""} /> {p.votes}
                  </button>
                  <span className="flex items-center gap-1.5 text-sm text-white/50">
                    <MessageSquare size={15} /> {p.comment_count}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-white/10 bg-[#0C0C14] text-white" data-testid="community-create-dialog">
          <DialogHeader>
            <DialogTitle>Start a discussion</DialogTitle>
            <DialogDescription>Share a tip, ask a question, or spark a conversation with the community.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input className={field} placeholder="Title" data-testid="post-title-input" maxLength={140}
              value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <textarea className={`${field} min-h-[120px] resize-y`} placeholder="What's on your mind?" data-testid="post-body-input" maxLength={4000}
              value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
            <select className={field} data-testid="post-topic-select"
              value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })}>
              {topics.map((t) => <option key={t} value={t} className="bg-[#0C0C14]">{t}</option>)}
            </select>
          </div>
          <DialogFooter>
            <Button onClick={submit} disabled={busy} data-testid="post-submit-btn"
              className="rounded-full bg-[#00FFC2] font-bold text-black hover:bg-[#00E0A8]">
              {busy ? "Posting…" : "Post"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
